import { useEffect, useState, useMemo } from "react";
import { Skeleton, Dropdown, Drawer } from "../../components/ui";
import swal from "../../utils/swal";
import {
  Search,
  Filter,
  Plus,
  MoreHorizontal,
  ChevronLeft,
  ChevronRight,
  Bell,
  SearchX,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  useDeleteNotification,
  useGetAllNotifications,
} from "../../query/notificationQuery";
import { useDebouncedValue } from "../../hooks/useDebouncedValue";
import DataBulkToolbar from "../../components/DataTable/DataBulkToolbar";
import DataTableCards from "../../components/DataTable/DataTableCards";
import { confirmBulkAction, runBulkIds, showBulkResult } from "../../utils/bulkActions";
import { NOTIFICATION_TYPES } from "./notificationHelpers";
import {
  NotificationTypeBadge,
  NotificationAudienceBadge,
  NotificationDeliveryBadge,
  NotificationTitleCell,
  NotificationDateCell,
} from "./NotificationParts";
import "../teachers/teachers.css";
import "./notifications.css";
import FilterSelect from "../../components/FilterSelect";

function pageWindow(current, totalPages) {
  if (totalPages <= 3) return Array.from({ length: totalPages }, (_, i) => i + 1);
  if (current <= 2) return [1, 2, 3];
  if (current >= totalPages - 1) return [totalPages - 2, totalPages - 1, totalPages];
  return [current - 1, current, current + 1];
}

function Notifications() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectedType, setSelectedType] = useState("");
  const [paginationConfig, setPaginationConfig] = useState({
    pageNumber: 1,
    limit: "10",
    keyword: "",
    type: "",
  });
  const debouncedKeyword = useDebouncedValue(searchTerm.trim(), 400);

  const queryParams = useMemo(
    () => ({
      page: String(paginationConfig.pageNumber),
      limit: String(paginationConfig.limit),
      keyword: paginationConfig.keyword,
      type: paginationConfig.type,
      source: "NOTICE",
    }),
    [paginationConfig]
  );

  const { isLoading, isFetching, data: notificationsData } = useGetAllNotifications(queryParams);
  const { mutate: deleteNotification, mutateAsync: deleteNotificationAsync } = useDeleteNotification();
  const [isBulkRunning, setIsBulkRunning] = useState(false);

  const docs = notificationsData?.docs || [];
  const pageSize = Number(paginationConfig.limit) || 10;
  const currentPage = Number(paginationConfig.pageNumber) || 1;
  const totalDocs = notificationsData?.totalDocs || 0;
  const totalPages = Math.max(1, Math.ceil(totalDocs / pageSize));
  const from = totalDocs === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const to = Math.min(currentPage * pageSize, totalDocs);
  const allSelected = docs.length > 0 && docs.every((row) => selectedIds.includes(row._id));
  const hasFilters = Boolean(paginationConfig.type);

  useEffect(() => {
    setPaginationConfig((prev) =>
      prev.keyword === debouncedKeyword ? prev : { ...prev, keyword: debouncedKeyword, pageNumber: 1 }
    );
  }, [debouncedKeyword]);

  useEffect(() => {
    if (!open) return;
    setSelectedType(paginationConfig.type || "");
  }, [open, paginationConfig.type]);

  const applyFilter = () => {
    setPaginationConfig((prev) => ({
      ...prev,
      pageNumber: 1,
      type: selectedType || "",
    }));
    setOpen(false);
  };

  const resetFilter = () => {
    setSelectedType("");
    setPaginationConfig((prev) => ({
      ...prev,
      pageNumber: 1,
      type: "",
    }));
    setOpen(false);
  };

  const handleDelete = (item) => {
    swal({
      title: "Recall this notice?",
      text: "It will be removed from every parent and teacher app. This cannot be undone.",
      icon: "warning",
      buttons: ["Keep notice", "Recall notice"],
      dangerMode: true,
    }).then((isConfirm) => {
      if (!isConfirm) return;
      deleteNotification(item._id, {
        onSuccess: () => swal("Recalled", "Notice removed from all users", "success"),
        onError: (error) => swal("Cannot recall", error?.message || "Failed to recall notice", "error"),
      });
    });
  };

  const handleBulkDelete = async () => {
    if (!selectedIds.length) return;
    const confirmed = await confirmBulkAction({
      actionLabel: "Recall selected",
      count: selectedIds.length,
      entityLabel: "notices",
    });
    if (!confirmed) return;

    setIsBulkRunning(true);
    const { success, failed } = await runBulkIds(selectedIds, deleteNotificationAsync);
    setSelectedIds([]);
    setIsBulkRunning(false);
    showBulkResult({ actionLabel: "Recall", success, failed });
  };

  return (
    <div className="data-page notifications-page">
      <div className="data-toolbar">
        <div className="data-toolbar-search">
          <label className="data-search">
            <Search size={16} />
            <input
              placeholder="Search here..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {isFetching && searchTerm ? <span className="data-search-spinner" aria-hidden /> : null}
          </label>
          <button
            type="button"
            className={`data-icon-btn${hasFilters ? " is-active" : ""}`}
            aria-label="Filter"
            onClick={() => setOpen(true)}
          >
            <Filter size={18} />
            {hasFilters ? <span className="data-filter-dot" /> : null}
          </button>

          <DataBulkToolbar
            selectedCount={selectedIds.length}
            onClear={() => setSelectedIds([])}
            disabled={isBulkRunning}
            actions={[
              { key: "delete", label: "Recall selected", tone: "danger", onClick: handleBulkDelete },
            ]}
          />
        </div>

        <div className="data-toolbar-actions">
          <button type="button" className="data-primary" onClick={() => navigate("/notifications/add")}>
            <Plus size={18} />
            New notice
          </button>
        </div>
      </div>

      <div className="data-card">
        {isLoading && !notificationsData ? (
          <div style={{ padding: 24 }}>
            <Skeleton active />
          </div>
        ) : docs.length === 0 ? (
          <div className="data-empty">
            <span className="data-empty-icon">
              {paginationConfig.keyword || hasFilters ? (
                <SearchX size={36} strokeWidth={1.6} />
              ) : (
                <Bell size={36} strokeWidth={1.6} />
              )}
            </span>
            <h3>{paginationConfig.keyword || hasFilters ? "No notices found" : "No notices yet"}</h3>
            <p>
              {paginationConfig.keyword || hasFilters
                ? "Try a different keyword or clear filters."
                : "Create a notice to send alerts or announcements."}
            </p>
          </div>
        ) : (
          <>
            <DataTableCards fetching={isFetching || isBulkRunning}>
              <table className="data-table notif-table has-row-check">
                <thead>
                  <tr>
                    <th>
                      <input
                        type="checkbox"
                        className="data-check"
                        checked={allSelected}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedIds((prev) => Array.from(new Set([...prev, ...docs.map((row) => row._id)])));
                          } else {
                            const pageIds = new Set(docs.map((row) => row._id));
                            setSelectedIds((prev) => prev.filter((id) => !pageIds.has(id)));
                          }
                        }}
                      />
                    </th>
                    <th className="notif-col-title">Notice</th>
                    <th className="notif-col-type">Type</th>
                    <th className="notif-col-audience">Audience</th>
                    <th className="notif-col-date">Created</th>
                    <th className="notif-col-action">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {docs.map((item) => {
                    const selected = selectedIds.includes(item._id);
                    const actionItems = [
                      {
                        key: "1",
                        label: <div style={{ padding: "8px 12px" }}>View</div>,
                        onClick: () => navigate(`/notifications/${item._id}`),
                      },
                      {
                        key: "2",
                        label: <div style={{ padding: "8px 12px" }}>Edit</div>,
                        onClick: () => navigate(`/notifications/edit/${item._id}`),
                      },
                      {
                        key: "3",
                        label: <div style={{ padding: "8px 12px", color: "#e83038" }}>Recall</div>,
                        onClick: () => handleDelete(item),
                      },
                    ];

                    return (
                      <tr
                        key={item._id}
                        className={`notif-row${selected ? " is-selected" : ""}`}
                        onClick={() => navigate(`/notifications/${item._id}`)}
                      >
                        <td onClick={(event) => event.stopPropagation()}>
                          <input
                            type="checkbox"
                            className="data-check"
                            checked={selected}
                            onChange={(e) =>
                              setSelectedIds((prev) =>
                                e.target.checked
                                  ? [...prev, item._id]
                                  : prev.filter((id) => id !== item._id)
                              )
                            }
                          />
                        </td>
                        <td className="notif-col-title">
                          <NotificationTitleCell notification={item} emphasizeUnread={false} />
                        </td>
                        <td className="notif-col-type" onClick={(event) => event.stopPropagation()}>
                          <NotificationTypeBadge type={item.type} />
                        </td>
                        <td className="notif-col-audience" onClick={(event) => event.stopPropagation()}>
                          <div className="notif-audience-cell">
                            <NotificationAudienceBadge sendTo={item.sendTo || "ALL"} notification={item} />
                            <NotificationDeliveryBadge notification={item} />
                          </div>
                        </td>
                        <td className="notif-col-date">
                          <NotificationDateCell createdAt={item.createdAt} />
                        </td>
                        <td className="notif-col-action" onClick={(event) => event.stopPropagation()}>
                          <Dropdown menu={{ items: actionItems }} trigger={["click"]} placement="bottomRight">
                            <button type="button" className="data-action" aria-label="More actions">
                              <MoreHorizontal size={20} />
                            </button>
                          </Dropdown>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </DataTableCards>

            <div className="data-pager">
              <p className="data-pager-text">
                Showing {from}-{to} from {totalDocs} data
              </p>
              <div className="data-pages">
                <button
                  type="button"
                  className="data-page-btn is-nav"
                  disabled={currentPage <= 1}
                  onClick={() =>
                    setPaginationConfig((prev) => ({ ...prev, pageNumber: Math.max(1, currentPage - 1) }))
                  }
                >
                  <ChevronLeft size={18} />
                </button>
                {pageWindow(currentPage, totalPages).map((page) => (
                  <button
                    key={page}
                    type="button"
                    className={`data-page-btn${page === currentPage ? " is-active" : ""}`}
                    onClick={() => setPaginationConfig((prev) => ({ ...prev, pageNumber: page }))}
                  >
                    {page}
                  </button>
                ))}
                <button
                  type="button"
                  className="data-page-btn is-nav"
                  disabled={currentPage >= totalPages}
                  onClick={() =>
                    setPaginationConfig((prev) => ({
                      ...prev,
                      pageNumber: Math.min(totalPages, currentPage + 1),
                    }))
                  }
                >
                  <ChevronRight size={18} />
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      <Drawer
        open={open}
        onClose={() => setOpen(false)}
        placement="right"
        title="Filter"
        width={400}
        footer={
          <>
            <button type="button" className="bw-drawer-btn is-ghost" onClick={resetFilter}>
              Clear All
            </button>
            <button type="button" className="bw-drawer-btn is-primary" onClick={applyFilter}>
              Apply
            </button>
          </>
        }
      >
        <div className="bw-filter-field">
          <p className="bw-filter-label">Type</p>
          <FilterSelect
            multiple
            value={selectedType}
            onChange={setSelectedType}
            options={[{ value: "", label: "All types" }, ...NOTIFICATION_TYPES]}
          />
        </div>
      </Drawer>
    </div>
  );
}

export default Notifications;
